import { PopoverGroup } from '@headlessui/react';
import ChevronIcon from '@otovo/shared/components/Icons/ChevronIcon/ChevronIcon';
import useLocalizedPath from '@otovo/shared/components/Navigation/useLocalizedPath';
import { useScrollTrigger } from '@otovo/shared/hooks/useIntersectionObserver';
import { Sanity$NavbarConfig } from '@otovo/shared/types/sanityTypes';
import cx from 'classnames';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import DropdownItem from '../FlyOutMenu/DropdownItem';
import PopoverBackdrop from '../FlyOutMenu/PopoverBackdrop';
import CompanyLink from './CompanyLink';
import DropdownContent from './DropdownContent';
import MenuButton from './MenuButton';
import MobileNavigation from './MobileNavigation';

type NavbarProps = {
  config: Sanity$NavbarConfig;
};

const Navbar = ({ config }: NavbarProps) => {
  const [solutionsOpen, setSolutionsOpen] = useState(false); // We store the state to propagate the background changes to the parent nav¨
  const [menuOpen, setMenuOpen] = useState(false); // TODO: Better way to solve this?

  // Fall back to a regular navbar on lack of browser support.
  const [fallbackStyles, setFallbackStyles] = useState(null);

  const [hasScrolledPast, isSupported] = useScrollTrigger(
    '#nav-scroll-trigger',
  );

  const isOpen = solutionsOpen || menuOpen;

  const colorScheme = hasScrolledPast
    ? 'color-scheme-white'
    : 'color-scheme-dark';

  const navStyle =
    isOpen || hasScrolledPast
      ? 'border-b border-b-heading/10 bg-surface shadow-[0px_6px_16px_0px_rgba(13,18,28,0.06)] duration-300'
      : 'border-b border-b-transparent bg-transparent duration-200';

  useEffect(() => {
    if (!isSupported) {
      setFallbackStyles(
        'color-scheme-white border-b border-b-heading/10 bg-surface shadow-[0px_6px_16px_0px_rgba(13,18,28,0.06)] duration-300',
      );
    }
  }, [isSupported]);

  const localizedAboutUsPath = useLocalizedPath('/about');

  return (
    <>
      <PopoverBackdrop isVisible={isOpen} />
      <PopoverGroup
        as="nav"
        className={cx(
          'fixed top-0 z-50 h-20 w-full items-center transition-all',
          fallbackStyles ? fallbackStyles : [navStyle, colorScheme],
        )}
      >
        <div className="my-4 flex max-w-8xl items-center justify-between px-4 md:px-6 lg:px-16 2xl:mx-auto">
          {/* Logo / branding */}
          <CompanyLink
            label={config.ariaLabels.homeLinkLabel}
            variant={
              fallbackStyles || colorScheme === 'color-scheme-white'
                ? 'blue'
                : 'white'
            }
          />
          {/* Main navigation links, hidden on mobile */}
          <ul className="hidden sm:flex sm:gap-2 md:gap-4">
            <li key="solutions-desktop">
              <DropdownItem
                handleOpen={setSolutionsOpen}
                menuItem={
                  <>
                    {config.topLevelLinkLabels.solutions}
                    <ChevronIcon className="h-4 w-4 rotate-90 group-data-[open]:-rotate-90" />
                  </>
                }
                className={colorScheme}
              >
                <DropdownContent
                  mainSection={config.solutionsSection.packagePageSection}
                  subSection={config.solutionsSection.productPageSection}
                  image={config.solutionsSection.image}
                />
              </DropdownItem>
            </li>
            <li key="pricing-desktop">
              <Link
                href="/pricing"
                className="flex items-center rounded-md px-3 py-2 text-base font-semibold text-heading"
              >
                {config.topLevelLinkLabels.pricing}
              </Link>
            </li>
            <li key="about-desktop">
              <Link
                href={`${localizedAboutUsPath}/`}
                className="flex items-center rounded-md px-3 py-2 text-base font-semibold text-heading"
              >
                {config.topLevelLinkLabels.aboutUs}
              </Link>
            </li>
          </ul>
          <DropdownItem
            className={colorScheme}
            menuItem={
              <>
                <span className="sr-only">
                  {config.ariaLabels.menuButtonLabel}
                </span>
                <MenuButton isOpen={menuOpen} />
              </>
            }
            handleOpen={setMenuOpen}
          >
            <div className="hidden md:block">
              <DropdownContent
                mainSection={config.mainSection.sections[0]}
                subSection={config.mainSection.sections?.[1] || undefined}
                image={config.mainSection.image}
              />
            </div>
            <div className="block md:hidden">
              <MobileNavigation config={config} />
            </div>
          </DropdownItem>
        </div>
      </PopoverGroup>
    </>
  );
};
export default Navbar;
